import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  TextField,
  Button,
  Box,
  Divider,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { db, auth } from "../firebase-config";
import { doc, collection, getDocs, getDoc, updateDoc, deleteDoc, addDoc } from "firebase/firestore";

const Cart = () => {
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submittingQuote, setSubmittingQuote] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [cartNote, setCartNote] = useState(""); // Stores the note
const [savingNote, setSavingNote] = useState(false);

  const currentUser = auth.currentUser;

  useEffect(() => {
    if (!currentUser) return;
  
    const fetchCartNote = async () => {
      try {
        const cartRef = doc(db, "carts", currentUser.uid);
        const cartSnap = await getDoc(cartRef);
  
        if (cartSnap.exists()) {
          setCartNote(cartSnap.data().note || ""); // Get note or set empty
        }
      } catch (err) {
        console.error("Error fetching cart note:", err);
      }
    };
  
    fetchCartNote();
  }, [currentUser]);

  

  useEffect(() => {
    if (!currentUser) return;

    const fetchUserCompanyId = async () => {
      try {
        const userRef = doc(db, "users", currentUser.uid);
        const userSnap = await getDoc(userRef);

        if (userSnap.exists()) {
          setCompanyId(userSnap.data().companyId || null);
        }
      } catch (err) {
        console.error("Error fetching user companyId:", err);
      }
    };

    fetchUserCompanyId();
  }, [currentUser]);

  useEffect(() => {
    if (!currentUser) return;

    const fetchCartItems = async () => {
      try {
        const cartRef = collection(db, "carts", currentUser.uid, "items");
        const snapshot = await getDocs(cartRef);
        const items = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
        setCartItems(items);
      } catch (err) {
        console.error("Error fetching cart:", err);
        setError("Failed to load cart items.");
      } finally {
        setLoading(false);
      }
    };

    fetchCartItems();
  }, [currentUser]);

  const handleQuantityChange = async (itemId, newQuantity) => {
    if (newQuantity < 1) return;

    try {
      const itemRef = doc(db, "carts", currentUser.uid, "items", itemId);
      await updateDoc(itemRef, { quantity: newQuantity });
      setCartItems((prev) =>
        prev.map((item) => (item.id === itemId ? { ...item, quantity: newQuantity } : item))
      );
    } catch (err) {
      console.error("Error updating quantity:", err);
      setError("Failed to update quantity.");
    }
  };

  const handleOptionChange = async (itemId, newOption) => {
    try {
      const itemRef = doc(db, "carts", currentUser.uid, "items", itemId);
      const item = cartItems.find((item) => item.id === itemId);

      if (!item) return;

      // Fetch product options to get price difference
      const productRef = doc(db, "products", item.productId);
      const productSnap = await getDoc(productRef);

      if (productSnap.exists()) {
        const productData = productSnap.data();
        const optionDetails = productData.options?.find((opt) => opt.name === newOption);
        const additionalPrice = optionDetails ? optionDetails.additionalPrice || 0 : 0;

        await updateDoc(itemRef, { selectedOption: newOption, price: item.basePrice + additionalPrice });
        setCartItems((prev) =>
          prev.map((item) =>
            item.id === itemId ? { ...item, selectedOption: newOption, price: item.basePrice + additionalPrice } : item
          )
        );
      }
    } catch (err) {
      console.error("Error updating option:", err);
      setError("Failed to update option.");
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      await deleteDoc(doc(db, "carts", currentUser.uid, "items", itemId));
      setCartItems((prev) => prev.filter((item) => item.id !== itemId));
    } catch (err) {
      console.error("Error removing item:", err);
      setError("Failed to remove item.");
    }
  };

  const handleRequestQuote = async () => {
    setSubmittingQuote(true);
    try {
      if (cartItems.length === 0) {
        setError("Cart is empty.");
        setSubmittingQuote(false);
        return;
      }

      let finalCompanyId = companyId;
      if (!finalCompanyId) {
        try {
          const userRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            finalCompanyId = userSnap.data().companyId || null;
            setCompanyId(finalCompanyId);
          }
        } catch (err) {
          console.error("Error fetching user companyId:", err);
        }
      }

      const quoteRef = collection(db, "quotes");
      await addDoc(quoteRef, {
        userId: currentUser.uid,
        userEmail: currentUser.email,
        companyId: finalCompanyId || null,
        status: "pending",
        note: cartNote,
        items: cartItems.map(({ id, name, price, quantity, selectedOption }) => ({
          productId: id,
          name,
          price,
          quantity,
          selectedOption: selectedOption || null,
        })),
        createdAt: new Date(),
      });

      for (const item of cartItems) {
        await deleteDoc(doc(db, "carts", currentUser.uid, "items", item.id));
      }
      setCartItems([]);
      alert("Quote requested successfully!");
    } catch (err) {
      console.error("Error submitting quote:", err);
      setError("Failed to request quote.");
    } finally {
      setSubmittingQuote(false);
    }
  };


  const handleSaveCartNote = async () => {
    setSavingNote(true);
    try {
      const cartRef = doc(db, "carts", currentUser.uid);
      await updateDoc(cartRef, { note: cartNote });
  
      alert("Note saved successfully!");
    } catch (err) {
      console.error("Error saving note:", err);
      setError("Failed to save note.");
    } finally {
      setSavingNote(false);
    }
  };

  

  const calculateTotalPrice = () => {
    return cartItems.reduce((total, item) => total + item.price * item.quantity, 0).toFixed(2);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">{error}</Typography>;

  return (
    <Container>
      <Typography variant="h4" gutterBottom>
        Your Cart
      </Typography>

      {cartItems.length === 0 ? (
        <Typography variant="body1">Your cart is empty.</Typography>
      ) : (
        <List>
          {cartItems.map((item, index) => (
            <React.Fragment key={item.id}>
              <ListItem alignItems="flex-start">
                <ListItemText
                  primary={item.name}
                  secondary={
                    <Typography variant="body2">
                      Price: ${item.price.toFixed(2)} | Total: ${(item.price * item.quantity).toFixed(2)}
                    </Typography>
                  }
                />

                {/* Option Selector */}
                {item.options && item.options.length > 0 && (
                  <FormControl sx={{ minWidth: 120, mr: 2 }}>
                    <Select
                      value={item.selectedOption || ""}
                      onChange={(e) => handleOptionChange(item.id, e.target.value)}
                      displayEmpty
                    >
                      <MenuItem value="">No Option</MenuItem>
                      {item.options.map((option, idx) => (
                        <MenuItem key={idx} value={option.name}>
                          {option.name} {option.additionalPrice ? `(+ $${option.additionalPrice})` : ""}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}

                <Box sx={{ display: "flex", alignItems: "center", marginRight: 2 }}>
                  <TextField
                    type="number"
                    value={item.quantity}
                    onChange={(e) => handleQuantityChange(item.id, parseInt(e.target.value))}
                    sx={{ width: 60, mr: 1 }}
                    inputProps={{ min: 1 }}
                  />
                </Box>
                <ListItemSecondaryAction>
                  <IconButton onClick={() => handleRemoveItem(item.id)} color="error">
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {index !== cartItems.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      )}




      {cartItems.length > 0 && (
        <Box sx={{ mt: 3, textAlign: "right" }}>
          <Typography variant="h6">Total Price: ${calculateTotalPrice()}</Typography>

          <Typography variant="body">This is just an estimated price, need to submit to get actual quote.</Typography>



          <Typography variant="h6"> Notes:</Typography>
  <TextField
    fullWidth
    multiline
    rows={3}
    value={cartNote}
    onChange={(e) => setCartNote(e.target.value)}
    placeholder="Add special instructions, delivery notes, etc."
    variant="outlined"
    sx={{ mt: 1 }}
  />

          <Button variant="contained" color="primary" onClick={handleRequestQuote} disabled={submittingQuote} sx={{ mt: 2 }}>
            {submittingQuote ? "Submitting..." : "Request a Quote"}
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default Cart;
